import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

import CouponForm from "./form";
import { ICouponCreateFormType } from "./form-type";

import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import { getAxiosError } from "@/utils/get-axios-error";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { IParams } from "@/types/params";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { useCreateCoupon, useUpdateCoupon, useGenerateCouponCode, useGetCouponById } from "@/api/hooks/useCoupons";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";

// Get user's timezone
const getUserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return "UTC";
    }
};

const userTimezone = getUserTimezone();

// Format date with timezone offset (e.g., 2024-02-24T09:48:00+03:30)
const formatDateWithTimezone = (dateString: string) => {
    const date = dayjs(dateString).tz(userTimezone);
    const offset = date.format("Z"); // Gets timezone offset like +03:30
    return date.format("YYYY-MM-DDTHH:mm:ss") + offset;
};

const CouponFormWrapper: React.FC = () => {
    const { id } = useParams<IParams>();
    const isEditingRule = Boolean(id);
    const navigate = useNavigate();
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const listAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.LIST);

    const { data: couponData } = useGetCouponById(id ? Number(id) : 0);
    const { mutate: UpdateCoupon } = useUpdateCoupon();
    const { mutate: CreateCoupon } = useCreateCoupon();
    const { mutate: generateCode } = useGenerateCouponCode();

    // Check if coupon can be updated
    useEffect(() => {
        if (isEditingRule && couponData?.data && !couponData.data.canUpdate) {
            enqueueSnackbar("This coupon cannot be edited because it has been used", { variant: "warning" });
            navigate(ROUTE_CONSTANTS.SETTINGS.COUPONS.ROOT.ABSOLUTE);
        }
    }, [isEditingRule, couponData, navigate]);

    const handleSubmit = async (val: ICouponCreateFormType, autoCodeCount?: number) => {
        try {
            setSubmitLoading(true);

            let finalCodes = val.codes?.filter((code) => code.trim() !== "") || [];

            // If auto mode is enabled and we're in create mode, generate codes first
            if (
                autoCodeCount &&
                !isEditingRule &&
                (!val.codes || val.codes.length === 0 || val.codes.every((code) => code.trim() === ""))
            ) {
                // This means we're in auto mode, generate codes first
                await new Promise((resolve, reject) => {
                    generateCode(autoCodeCount, {
                        onSuccess: (data) => {
                            finalCodes = data.data; // data.data is already an array of strings
                            resolve(data);
                        },
                        onError: (error) => {
                            console.error("🚀 Error generating codes:", error);
                            reject(error);
                        },
                    });
                });
            } else {
                console.log("🚀 Custom mode or codes already exist");
            }

            const payload: any = {
                title: val.title,
                amountType: val.amountType,
                amountValue: Number(val.amountValue),
                appliedTo: val.appliedTo,
                // Only include active field in create mode
                ...(isEditingRule ? {} : { active: val.active }),
            };

            // Add codes only in create mode
            if (!isEditingRule) {
                payload.codes = finalCodes;
            }

            // Only add optional fields if they have values
            if (val.description) payload.description = val.description;
            if (val.showStartDate && val.startDate) payload.startDate = formatDateWithTimezone(val.startDate);
            if (val.expireDate) payload.expireDate = formatDateWithTimezone(val.expireDate);
            if (val.showUsagePerUser && val.usagePerUser) payload.usagePerUser = Number(val.usagePerUser);
            if (val.showUsageLimit && val.usageLimit) payload.usageLimit = Number(val.usageLimit);
            if (val.showMinimumPrice && val.minimumPrice) payload.minimumPrice = Number(val.minimumPrice);
            if (val.plans && val.plans.length > 0)
                payload.plans = val.plans.map((plan: any) => (typeof plan === "object" ? plan.id : plan));

            if (isEditingRule) {
                UpdateCoupon(
                    { id: Number(id), body: payload },
                    {
                        onSuccess: () => {
                            navigate(ROUTE_CONSTANTS.SETTINGS.COUPONS.ROOT.ABSOLUTE);
                        },
                        onError: (err) => {
                            const error = getAxiosError(err);
                            const message = error?.meta.message || "Server Error";
                            enqueueSnackbar(message, { variant: "error" });
                        },
                    }
                );
            } else {
                CreateCoupon(payload, {
                    onSuccess: () => {
                        navigate(ROUTE_CONSTANTS.SETTINGS.COUPONS.ROOT.ABSOLUTE);
                    },
                    onError: (err) => {
                        const error = getAxiosError(err);
                        const message = error?.meta.message || "Server Error";
                        enqueueSnackbar(message, { variant: "error" });
                    },
                });
            }
        } catch (err) {
            const error = getAxiosError(err);
            const message = error?.meta.message || "Server Error";
            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <DashboardHeader
                title={`${isEditingRule ? "Update" : "Create New"} Coupon`}
                {...(listAccess
                    ? {
                          titleSuffix: (
                              <Button component={Link} to={ROUTE_CONSTANTS.SETTINGS.COUPONS.ROOT.ABSOLUTE}>
                                  Back To Coupons List
                              </Button>
                          ),
                      }
                    : undefined)}
            />
            <DashboardContent>
                <CouponForm loading={submitLoading} onSubmit={handleSubmit} />
            </DashboardContent>
        </>
    );
};

export default CouponFormWrapper;
